import React, { FunctionComponent, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Redirect, useLocation, withRouter } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { Button, Header, Form, Message, Card } from 'semantic-ui-react';
import FormInput from 'components/FormComponents/FormInput';
import withStyles from 'react-jss';
import { compose } from 'recompose';
import Loading from 'components/Loading';
import { ClassProp } from 'helpers/types';

import {
  noBlankPasswords,
  passwordDoesNotMeetRequirements,
  passwordRegex,
  passwordsDoNotMatch,
  sanitizeMessage,
} from 'components/UserMgmt/UserMgmt.helpers';
import styles from 'components/UserMgmt/UserMgmt.styles';
import PasswordInput from 'components/FormComponents/PasswordInput';
import EmailInput from 'components/FormComponents/EmailInput';

export type Values = {
  code: string;
  email: string;
  newPassword: string;
};

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
};

type NewPasswordForm = {
  email: string;
  code: string;
  newPassword: string;
};

type Status = {
  submitting: boolean;
  errors?: string;
  success: boolean;
};

const initialStatus: Status = {
  submitting: false,
  errors: undefined,
  success: false,
};

const NewPassword: FunctionComponent<Props> = ({ classes }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get('code') || '';

  const [status, setStatus] = useState<Status>(initialStatus);

  const methods = useForm<NewPasswordForm>({
    mode: 'onBlur',
    defaultValues: {
      code,
    },
  });

  const { register, handleSubmit, watch, formState } = methods;

  const watchPassword = watch('newPassword');

  const onSubmit = async ({ email, code, newPassword }: NewPasswordForm) => {
    setStatus({ ...status, submitting: true });
    try {
      await Auth.forgotPasswordSubmit(email, code, newPassword);
      setStatus({ submitting: false, success: true, errors: undefined });
    } catch (error) {
      const errorMsg = sanitizeMessage(
        error,
        'Incorrect username or password.',
      );
      setStatus({ submitting: false, success: false, errors: errorMsg });
    }
  };

  if (status && status.success) {
    return <Redirect to="/login" />;
  }

  const { submitting, errors, success } = status;

  return (
    <>
      <Header className={classes.header} as="h1">
        Create New Password
      </Header>
      <Card className={classes.container} fluid centered>
        <Card.Content>
          {submitting && <Loading loading />}
          <FormProvider {...methods}>
            <Form onSubmit={handleSubmit(onSubmit)} error={!!errors}>
              {errors && !success && <Message error content={errors} />}

              <Form.Field className={classes.field}>
                <FormInput
                  label="Verification Code"
                  name="code"
                  type="text"
                  register={register({ required: 'Code may not be blank.' })}
                  placeholder="Enter your Verification Code"
                />
              </Form.Field>
              <Form.Field className={classes.field}>
                <EmailInput
                  label="Email"
                  name="email"
                  placeholder="Enter your Email"
                  autoComplete="username"
                />
              </Form.Field>
              <Form.Field className={classes.field}>
                <PasswordInput
                  label="Password"
                  name="newPassword"
                  autoComplete="new-password"
                  placeholder="Enter your New Password"
                  register={register({
                    required: noBlankPasswords,
                    pattern: {
                      value: passwordRegex,
                      message: passwordDoesNotMeetRequirements,
                    },
                  })}
                />
              </Form.Field>
              <Form.Field className={classes.field}>
                <PasswordInput
                  label="Confirm Password"
                  name="confirmPassword"
                  autoComplete="new-password"
                  placeholder="Re-enter your New Password"
                  register={register({
                    validate: (value) =>
                      value === watchPassword || passwordsDoNotMatch,
                  })}
                />
              </Form.Field>
              <Form.Field>
                <Button
                  fluid
                  type="submit"
                  className={classes.button}
                  disabled={submitting || !formState.isValid}
                >
                  Update Password
                </Button>
              </Form.Field>
            </Form>
          </FormProvider>
        </Card.Content>
      </Card>
    </>
  );
};

export default compose<Props, any>(withRouter, withStyles(styles))(NewPassword);
